<script setup lang="ts">
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete'
import { useToast } from '@/shared/composables/UseToast'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import { useDataTableFilters } from '@/shared/composables/UseDataTableFilters'
import IscoCodePickerDialog from '../dialogs/IscoCodePickerDialog.vue'
import MergeJobFunctionDialog from '../dialogs/MergeJobFunctionDialog.vue'
import {
  useAdminListJobFunctions,
  useAdminCreateJobFunction,
  useAdminUpdateJobFunction,
  useAdminHideJobFunction,
  useAdminUnhideJobFunction,
  adminClassifyJobFunctionIsco,
  getAdminListJobFunctionsQueryKey,
  useListJobCategories,
  useAdminListIscoGroups,
} from '@swipe4work/api-orval'
import type { AdminJobFunctionCrudJobFunctionDto } from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
definePage({
  path: '/data/job-functions',
  name: '/data/JobFunctions',
  meta: {
    title: 'Functies',
  },
})

const toast = useToast()
const queryClient = useQueryClient()
const dialog = useDynamicDialog()

function openIscoPicker(
  jobFunction: AdminJobFunctionCrudJobFunctionDto,
  suggestion?: {
    code: string,
    label: string | null,
    score: number | null,
    source: string
  } | null,
) {
  dialog.open(IscoCodePickerDialog, {
    props: {
      header: 'ISCO-code toewijzen',
      style: {
        width: '90vw',
        height: '90vw',
      },
    },
    data: {
      entityType: 'jobFunction',
      entityId: jobFunction.id,
      entityName: jobFunction.name,
      currentIsco4: jobFunction.isco4 ?? null,
      suggestion,
    },
  })
}

function openMergeDialog(jobFunction: AdminJobFunctionCrudJobFunctionDto) {
  dialog.open(MergeJobFunctionDialog, {
    props: { header: 'Functies samenvoegen' },
    data: {
      source: {
        id: jobFunction.id,
        name: jobFunction.name, 
      },
      initialTargetId: null,
    },
  })
}

const classifyingId = ref<number | null>(null)

async function classifyIsco(jobFunction: AdminJobFunctionCrudJobFunctionDto) {
  classifyingId.value = jobFunction.id
  try {
    const result = await adminClassifyJobFunctionIsco(jobFunction.id)
    if (!result.isco4) {
      toast.error('Geen suggestie', 'De AI kon geen ISCO-code voorstellen voor deze functie.')

      return
    }

    openIscoPicker(jobFunction, {
      code: result.isco4,
      label: result.escoLabel ?? result.iscoUnitGroup?.preferredLabel ?? null,
      score: result.score ?? null,
      source: result.source,
    })
  }
  catch {
    toast.error('Fout', 'Er is iets misgegaan bij het classificeren.')
  }
  finally {
    classifyingId.value = null
  }
}

const { data: categoriesData } = useListJobCategories()
const { data: iscoGroupsData } = useAdminListIscoGroups({ query: { staleTime: Infinity } })

const categories = computed(() => categoriesData.value?.jobCategories ?? [])

interface IscoUnitGroupOption {
  code: string
  label: string
}

const iscoUnitGroupOptions = computed<IscoUnitGroupOption[]>(() =>
  (iscoGroupsData.value?.iscoGroups ?? [])
    .filter((g) => g.level === 4)
    .map((g) => ({
      code: g.code,
      label: `${g.code}: ${g.preferredLabel}`,
    })),
)

// Derived filter options
const NO_CATEGORY_ID = -1

const categoryFilterOptions = computed(() => [
  {
    id: NO_CATEGORY_ID,
    name: 'Geen beroepsgroep',
  },
  ...categories.value,
])

type IscoPresence = 'all' | 'with' | 'without'
const iscoPresenceOptions: {
  label: string,
  value: IscoPresence
}[] = [
  {
    label: 'Alle ISCO',
    value: 'all',
  },
  {
    label: 'Met ISCO',
    value: 'with',
  },
  {
    label: 'Zonder ISCO',
    value: 'without',
  },
]

type HiddenPresence = 'all' | 'hidden' | 'visible'
const hiddenPresenceOptions: {
  label: string,
  value: HiddenPresence
}[] = [
  {
    label: 'Alles',
    value: 'all',
  },
  {
    label: 'Verborgen',
    value: 'hidden',
  },
  {
    label: 'Zichtbaar',
    value: 'visible',
  },
]

const PAGE_SIZE = 25

type FilterState = {
  categoryFilter: number[]
  iscoPresence: IscoPresence
  iscoCode: string
  hiddenPresence: HiddenPresence
  idealOnly: boolean
}

const {
  filters,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  onSort,
  onPage,
} = useDataTableFilters<FilterState>({
  schema: {
    categoryFilter: 'ids',
    iscoPresence: 'enum',
    iscoCode: 'string',
    hiddenPresence: 'enum',
    idealOnly: 'boolean',
  },
  defaultFilters: () => ({
    categoryFilter: [],
    iscoPresence: 'all',
    iscoCode: '',
    hiddenPresence: 'visible',
    idealOnly: false,
  }),
  defaultPageSize: PAGE_SIZE,
  rowsPerPageOptions: [
    10,
    25,
    50,
    100,
  ],
  persistToStorage: true,
})

const params = computed(() => ({
  search: search.value,
  iscoCode: filters.iscoCode.trim() || undefined,
  hasIsco: filters.iscoPresence === 'all' ? undefined : filters.iscoPresence === 'with',
  isHidden: filters.hiddenPresence === 'all' ? undefined : filters.hiddenPresence === 'hidden',
  isIdeal: filters.idealOnly || undefined,
  jobCategoryIds: filters.categoryFilter.filter((id) => id !== NO_CATEGORY_ID),
  includeUncategorized: filters.categoryFilter.includes(NO_CATEGORY_ID),
  sortField: sortField.value,
  sortDescending: sortDescending.value ? true : undefined,
  Limit: rows.value,
  Offset: offset.value,
}))

const { data: listData, isPending, isError } = useAdminListJobFunctions(params)

const jobFunctions = computed(() => listData.value?.jobFunctions ?? [])
const totalCount = computed(() => listData.value?.totalCount ?? 0)

// Create
const showCreateForm = ref(false)
const newName = ref('')
const newJobCategoryIds = ref<number[]>([])
const newSuggestedOrder = ref<number | null>(null)
const newIscoUnitGroup = ref<IscoUnitGroupOption | null>(null)
const iscoUnitGroupSuggestions = ref<IscoUnitGroupOption[]>([])

function searchIscoUnitGroups(event: AutoCompleteCompleteEvent) {
  const query = event.query.trim().toLowerCase()

  iscoUnitGroupSuggestions.value = query
    ? iscoUnitGroupOptions.value.filter((g) => g.label.toLowerCase().includes(query)).slice(0, 20)
    : iscoUnitGroupOptions.value.slice(0, 20)
}

const createMutation = useAdminCreateJobFunction({
  mutation: {
    onSuccess: () => {
      toast.success('Aangemaakt', 'De functie is aangemaakt.')
      queryClient.invalidateQueries({ queryKey: getAdminListJobFunctionsQueryKey() })
      showCreateForm.value = false
      newName.value = ''
      newJobCategoryIds.value = []
      newSuggestedOrder.value = null
      newIscoUnitGroup.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het aanmaken.')
    },
  },
})

function submitCreate() {
  if (!newName.value.trim() || newJobCategoryIds.value.length === 0) {
    return
  }

  createMutation.mutate({
    data: {
      name: newName.value.trim(),
      jobCategoryIds: newJobCategoryIds.value,
      suggestedOrder: newSuggestedOrder.value,
      iscoUnitGroupCode: newIscoUnitGroup.value?.code ?? null,
    },
  })
}

// Edit
const editingRow = ref<AdminJobFunctionCrudJobFunctionDto | null>(null)
const editName = ref('')
const editJobCategoryIds = ref<number[]>([])
const editSuggestedOrder = ref<number | null>(null)

function startEdit(jobFunction: AdminJobFunctionCrudJobFunctionDto) {
  editingRow.value = jobFunction
  editName.value = jobFunction.name
  editJobCategoryIds.value = [ ...jobFunction.jobCategoryIds ]
  editSuggestedOrder.value = jobFunction.suggestedOrder ?? null
}

function cancelEdit() {
  editingRow.value = null
}

const updateMutation = useAdminUpdateJobFunction({
  mutation: {
    onSuccess: () => {
      toast.success('Bijgewerkt', 'De functie is bijgewerkt.')
      queryClient.invalidateQueries({ queryKey: getAdminListJobFunctionsQueryKey() })
      editingRow.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het bijwerken.')
    },
  },
})

function submitEdit() {
  if (!editingRow.value || !editName.value.trim() || editJobCategoryIds.value.length === 0) {
    return
  }

  updateMutation.mutate({
    data: {
      id: editingRow.value.id,
      name: editName.value.trim(),
      jobCategoryIds: editJobCategoryIds.value,
      suggestedOrder: editSuggestedOrder.value,
    },
  })
}

function getCategoryNames(ids: number[]): string {
  return ids.map((id) => categories.value.find((c) => c.id === id)?.name ?? String(id)).join(', ')
}

// Hide / unhide
const hidingId = ref<number | null>(null)

const hideMutation = useAdminHideJobFunction({
  mutation: {
    onSuccess: () => {
      toast.success('Verborgen', 'De functie is verborgen voor selectie.')
      queryClient.invalidateQueries({ queryKey: getAdminListJobFunctionsQueryKey() })
    },
    onError: () => toast.error('Fout', 'Er is iets misgegaan bij het verbergen.'),
    onSettled: () => { hidingId.value = null },
  },
})

const unhideMutation = useAdminUnhideJobFunction({
  mutation: {
    onSuccess: () => {
      toast.success('Zichtbaar', 'De functie is weer selecteerbaar.')
      queryClient.invalidateQueries({ queryKey: getAdminListJobFunctionsQueryKey() })
    },
    onError: () => toast.error('Fout', 'Er is iets misgegaan bij het zichtbaar maken.'),
    onSettled: () => { hidingId.value = null },
  },
})

function toggleHidden(jobFunction: AdminJobFunctionCrudJobFunctionDto) {
  hidingId.value = jobFunction.id
  if (jobFunction.isHidden) {
    unhideMutation.mutate({ id: jobFunction.id })
  }
  else {
    hideMutation.mutate({ id: jobFunction.id })
  }
}
</script>

<template>
  <div class="space-y-5">
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">
              Functies
            </h3>
            <Button
              label="Nieuwe functie"
              icon="fa-regular fa-plus"
              @click="showCreateForm = !showCreateForm"
            />
          </div>

          <!-- Create form -->
          <div v-if="showCreateForm" class="border border-surface-200 rounded bg-surface-50 p-4">
            <h4 class="mb-3 font-semibold">
              Nieuwe functie toevoegen
            </h4>
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-1">
                <label for="new-name" class="text-sm font-medium">Naam *</label>
                <InputText
                  id="new-name"
                  v-model="newName"
                  placeholder="Bijv. Software Engineer"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label for="new-category" class="text-sm font-medium">Beroepsgroepen *</label>
                <MultiSelect
                  id="new-category"
                  v-model="newJobCategoryIds"
                  :options="categories"
                  option-label="name"
                  option-value="id"
                  placeholder="Selecteer beroepsgroepen"
                  class="w-full"
                />
              </div>
              <div class="flex flex-col gap-1">
                <label for="new-isco" class="text-sm font-medium">ISCO-code</label>
                <AutoComplete
                  input-id="new-isco"
                  v-model="newIscoUnitGroup"
                  :suggestions="iscoUnitGroupSuggestions"
                  option-label="label"
                  placeholder="Zoek op ISCO-code of label..."
                  dropdown
                  class="w-full"
                  input-class="w-full"
                  @complete="searchIscoUnitGroups"
                />
                <small class="text-surface-400">Optioneel. Koppelt de functie direct aan een ISCO-4 eenheidsgroep.</small>
              </div>
              <div class="flex flex-col gap-1">
                <label for="new-suggested-order" class="text-sm font-medium">Suggestievolgorde</label>
                <InputNumber
                  input-id="new-suggested-order"
                  v-model="newSuggestedOrder"
                  :min="1"
                  placeholder="Leeg = niet voorgesteld"
                  class="w-full"
                />
                <small class="text-surface-400">Rang in de generieke onboarding-suggesties. Leeg laten om niet voor te stellen.</small>
              </div>
              <div class="flex justify-end gap-2">
                <Button
                  label="Annuleren"
                  severity="secondary"
                  @click="showCreateForm = false"
                />
                <Button
                  label="Aanmaken"
                  :disabled="!newName.trim() || newJobCategoryIds.length === 0 || createMutation.isPending.value"
                  :loading="createMutation.isPending.value"
                  @click="submitCreate"
                />
              </div>
            </div>
          </div>

          <!-- Filters row -->
          <div class="flex flex-wrap gap-3">
            <IconField class="min-w-48 flex-1">
              <InputIcon class="fa-regular fa-search" />
              <InputText v-model="searchInput" placeholder="Zoeken op naam..." class="w-full" />
            </IconField>
            <IconField class="min-w-40">
              <InputIcon class="fa-regular fa-hashtag" />
              <InputText v-model="filters.iscoCode" placeholder="ISCO-08 code..." class="w-full" />
            </IconField>
            <Select
              v-model="filters.iscoPresence"
              :options="iscoPresenceOptions"
              option-label="label"
              option-value="value"
              class="min-w-44"
            />
            <Select
              v-model="filters.hiddenPresence"
              :options="hiddenPresenceOptions"
              option-label="label"
              option-value="value"
              class="min-w-44"
            />
            <label class="flex cursor-pointer items-center gap-2 text-sm text-surface-700">
              <ToggleSwitch v-model="filters.idealOnly" input-id="ideal-only" />
              Alleen Ideal-functies
            </label>
            <MultiSelect
              v-model="filters.categoryFilter"
              :options="categoryFilterOptions"
              option-label="name"
              option-value="id"
              placeholder="Beroepsgroep"
              show-clear
              class="min-w-52"
            />
          </div>

          <!-- Table -->
          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van de functies.
          </Message>
          <DataTable
            v-else
            :value="jobFunctions"
            :lazy="true"
            :first="offset"
            :rows="rows"
            :total-records="totalCount"
            :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
            :sort-field="sortField"
            :sort-order="sortDescending ? -1 : 1"
            paginator
            class="w-full"
            @page="onPage"
            @sort="onSort"
          >
            <Column field="name" header="Naam" sortable>
              <template #body="{ data: jobFunction }">
                <div v-if="editingRow?.id === jobFunction.id">
                  <InputText v-model="editName" class="w-full" />
                </div>
                <span v-else class="flex items-center gap-2">
                  {{ jobFunction.name }}
                  <Tag v-if="jobFunction.isHidden" value="Verborgen" severity="secondary" />
                </span>
              </template>
            </Column>
            <Column field="isco4" header="ISCO" sortable>
              <template #body="{ data: jobFunction }">
                <template v-if="jobFunction.isco4">
                  <span class="text-sm font-mono">{{ jobFunction.isco4 }}</span>
                  <span class="ml-1 text-xs text-surface-400">({{ jobFunction.isco1 }})</span>
                  <i
                    v-if="jobFunction.isIdeal"
                    v-tooltip.top="'Ideal-functie: ISCO-groep is een Ideal Occupation'"
                    class="fa-solid fa-star ml-2 text-xs text-primary"
                  />
                </template>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column header="Beroepsgroepen">
              <template #body="{ data: jobFunction }">
                <div v-if="editingRow?.id === jobFunction.id">
                  <MultiSelect
                    v-model="editJobCategoryIds"
                    :options="categories"
                    option-label="name"
                    option-value="id"
                    placeholder="Selecteer beroepsgroepen"
                    class="w-full"
                  />
                </div>
                <span v-else-if="jobFunction.jobCategoryIds.length > 0">{{ getCategoryNames(jobFunction.jobCategoryIds) }}</span>
                <span v-else class="text-sm text-surface-400">Geen</span>
              </template>
            </Column>
            <Column field="activeJobCount" header="Actieve vacatures" sortable style="width: 140px">
              <template #body="{ data: jobFunction }">
                <span v-if="jobFunction.activeJobCount > 0" class="font-medium">
                  {{ jobFunction.activeJobCount }}
                </span>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column field="suggestedOrder" sortable style="width: 130px">
              <template #header>
                <span class="flex items-center gap-1">
                  Voorgesteld
                  <i
                    class="fa-regular fa-circle-info text-surface-400"
                    v-tooltip.top="'Rang in de generieke onboarding-suggesties die kandidaten te zien krijgen. Lager = eerder voorgesteld. Leeg = niet voorgesteld.'"
                  />
                </span>
              </template>
              <template #body="{ data: jobFunction }">
                <InputNumber
                  v-if="editingRow?.id === jobFunction.id"
                  v-model="editSuggestedOrder"
                  :min="1"
                  placeholder="Leeg"
                  class="w-full"
                />
                <span v-else-if="jobFunction.suggestedOrder != null" class="font-medium">
                  {{ jobFunction.suggestedOrder }}
                </span>
                <span v-else class="text-sm text-surface-400">—</span>
              </template>
            </Column>
            <Column header="Acties" style="width: 200px">
              <template #body="{ data: jobFunction }">
                <div v-if="editingRow?.id === jobFunction.id" class="flex gap-2">
                  <Button
                    icon="fa-regular fa-check"
                    severity="success"
                    size="small"
                    v-tooltip.top="'Opslaan'"
                    :loading="updateMutation.isPending.value"
                    @click="submitEdit"
                  />
                  <Button
                    icon="fa-regular fa-xmark"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Annuleren'"
                    @click="cancelEdit"
                  />
                </div>
                <div v-else class="flex gap-2">
                  <Button
                    icon="fa-regular fa-pen"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Bewerken'"
                    @click="startEdit(jobFunction)"
                  />
                  <Button
                    icon="fa-regular fa-sitemap"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'ISCO-code toewijzen'"
                    @click="openIscoPicker(jobFunction)"
                  />
                  <Button
                    icon="fa-regular fa-sparkles"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Classificeren met AI'"
                    :loading="classifyingId === jobFunction.id"
                    @click="classifyIsco(jobFunction)"
                  />
                  <Button
                    icon="fa-regular fa-code-merge"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Samenvoegen met...'"
                    @click="openMergeDialog(jobFunction)"
                  />
                  <Button
                    :icon="jobFunction.isHidden ? 'fa-regular fa-eye' : 'fa-regular fa-eye-slash'"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="jobFunction.isHidden ? 'Zichtbaar maken' : 'Verbergen voor selectie'"
                    :loading="hidingId === jobFunction.id"
                    @click="toggleHidden(jobFunction)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>
  </div>
</template>
